/**
 * Item Readiness Queue
 *
 * Queue-only view listing items that need structure before they can be ranked.
 * Clicking an item navigates to the Item Detail page for full editing.
 *
 * Previously named "ItemShaping" — editing has moved to ItemDetailPage /
 * ItemDetailEditable. This component is now a lightweight queue browser.
 */

import { useState, useMemo } from 'react';
import { ListChecks, ExternalLink } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { useNavigate } from 'react-router-dom';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useTriageStore } from '../../../stores/prioritization/triageStore';
import { getLifecycleStateColors } from '../../../styles/prioritization/roleTokens';
import { TriageStatusBadge } from '../TriageStatusBadge';
import { useAltitudeText } from '../../../hooks/prioritization/useAltitudeText';

export function ItemShaping() {
  const altitudeText = useAltitudeText();
  const navigate = useNavigate();
  const [selectedQueueFilter, setSelectedQueueFilter] = useState<string>('all');
  const [showNonTriaged, setShowNonTriaged] = useState(false);

  const items = useItemsStore((s) => s.items);
  const getTriageAssessment = useTriageStore((s) => s.getTriageAssessment);

  // Queue filtering
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const availableQueues = useMemo(() => {
    return Array.from(orgUnits.values())
      .filter((ou) => ou.queueSettings?.enabled)
      .map((ou) => ({ id: ou.id, label: ou.displayName }));
  }, [orgUnits]);

  const itemsToShape = useMemo(() => {
    return items.filter((c) => {
      const detailedState = c.detailedState ?? (c as { detailed_state?: string }).detailed_state;
      const excludedStates = ['DEFERRED', 'REJECTED', 'NEEDS_CLARIFICATION'];
      if (detailedState && excludedStates.includes(detailedState)) return false;
      if (detailedState === 'SHAPED') return false;

      const shapingStates = ['PROTO_NEW', 'RAW', 'SHAPING', 'TRIAGED', 'DIRECT'];
      const hasShapingDetailedState = detailedState && shapingStates.includes(detailedState);
      const isInIntake = c.lifecycle_stage === 'intake';
      const isShapingItem =
        c.lifecycle_state === 'item' &&
        detailedState &&
        ['RAW', 'SHAPING', 'TRIAGED', 'DIRECT'].includes(detailedState);

      if (!hasShapingDetailedState && !isInIntake && !isShapingItem) return false;

      if (selectedQueueFilter !== 'all') {
        if (c.queueId !== selectedQueueFilter) return false;
      }

      if (!showNonTriaged && detailedState !== 'SHAPING' && detailedState !== 'DIRECT') {
        const assessment = getTriageAssessment(c.id);
        if (!assessment && detailedState !== 'PROTO_NEW') return false;
      }

      return true;
    });
  }, [items, selectedQueueFilter, showNonTriaged, getTriageAssessment]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<ListChecks className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Readiness Queue"
        subtitle="Items that need structure before they can be prioritized. Click an item to open its detail page."
      />

      {/* Filter Bar */}
      <div className="flex items-center gap-6 flex-wrap">
        {availableQueues.length > 0 && (
          <div className="flex items-center gap-3">
            <label htmlFor="queue-filter" className="text-sm font-medium text-[var(--text-sec)]">
              Filter by Queue:
            </label>
            <select
              id="queue-filter"
              value={selectedQueueFilter}
              onChange={(e) => setSelectedQueueFilter(e.target.value)}
              className="px-3 py-1.5 border border-[var(--rule)] text-sm bg-[var(--bg-raised)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Queues</option>
              {availableQueues.map((queue) => (
                <option key={queue.id} value={queue.id}>
                  {queue.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showNonTriaged}
            onChange={(e) => setShowNonTriaged(e.target.checked)}
            className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-500"
          />
          <span className="text-[var(--text-sec)]">Show non-triaged items</span>
        </label>

        <span className="text-sm text-[var(--text-ter)]">
          {itemsToShape.length} item{itemsToShape.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Item List */}
      {itemsToShape.length === 0 ? (
        <div className="card text-center py-12 text-[var(--text-ter)]">
          <ListChecks className="h-12 w-12 mx-auto mb-3 text-[var(--text-ter)]" />
          <p className="text-sm">No items need structure right now</p>
          <p className="text-xs mt-1">Capture some items in Intake first</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {itemsToShape.map((it) => {
            const stateColor = getLifecycleStateColors(it.lifecycle_state);
            const detailedState =
              it.detailedState ?? (it as { detailed_state?: string }).detailed_state;

            return (
              <button
                key={it.id}
                onClick={() => navigate(`/prioritization/items/${it.id}`)}
                className="card text-left p-4 hover:border-primary-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-[var(--text-pri)] text-sm truncate flex-1 mr-2">
                    {it.title}
                  </h3>
                  <ExternalLink className="h-4 w-4 text-[var(--text-ter)] group-hover:text-primary-500 flex-shrink-0 transition-colors" />
                </div>

                {it.description && (
                  <p className="text-xs text-[var(--text-ter)] line-clamp-2 mb-3">{it.description}</p>
                )}

                {/* Badges */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className={`inline-block text-xs px-2 py-0.5 rounded-full ${stateColor.badge}`}
                  >
                    {it.lifecycle_state || 'unknown'}
                  </span>
                  <TriageStatusBadge
                    itemId={it.id}
                    size="sm"
                    isDirect={
                      detailedState === 'DIRECT' ||
                      (it.metadata as { submittedDirectly?: boolean } | undefined)
                        ?.submittedDirectly === true
                    }
                  />
                  {it.altitudeLevel && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded-full text-xs">
                      <span>{altitudeText.format(it.altitudeLevel, it.itemCategory)}</span>
                    </span>
                  )}
                  {it.itemCategory && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium capitalize bg-[var(--hover-tint)] text-[var(--text-sec)]">
                      {it.itemCategory}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
